import { attr, computedOf, html, MapSignal, on, prop } from '@tempots/dom'
import { renderDesignToHtml } from '@rh/shared/templates/design-renderer'
import { PAGE_SIZES, type SavedDesign } from '@rh/shared/schemas/design'
import type { TokenResolutionData } from '@rh/shared/templates/design-tokens'
import { AgencyAssetService } from '@/services/agency-asset-service'
import { ScaledPage } from '@/components/scaled-page'
import { wizardTokenData, type WizardState } from '../wizard-state'

/**
 * Shared final step for both paths: the finished design rendered through
 * the exact renderDesignToHtml pipeline the server uses, with live token
 * data so the user sees THEIR values, not {{placeholders}}.
 *
 * The "Preview as" toggle is where recruiter personalization is TAUGHT:
 * switching to a demo teammate re-renders the same design with their
 * details, showing (not explaining) that recruiter fields aren't
 * hard-coded. The footer's "Open in editor" button (in wizard.ts)
 * performs the hand-off.
 */
export function PreviewStep(state: WizardState, agencyId: string) {
  const resolveImageUrl = (assetKey: string) =>
    AgencyAssetService.getDesignImageAssetUrl(agencyId, assetKey)

  const viewAs = prop<'you' | 'teammate'>('you')

  return MapSignal(
    computedOf(state.result, viewAs)((design, who) => ({ design, who })),
    ({ design, who }) => {
      if (!design) {
        return html.div(
          attr.class('wiz-error'),
          'Something went wrong building the design — go Back and try again.'
        )
      }
      const tokenData = wizardTokenData(state)
      const personalized = hasRecruiterTokens(design)
      const rendered = renderDesignToHtml(design, 'first', {
        tokenData:
          who === 'teammate'
            ? { ...tokenData, recruiter: sampleTeammate(tokenData) }
            : tokenData,
        resolveImageUrl,
      })
      const size = PAGE_SIZES[design.pageSize]
      const pageSize = prop({ w: size.w, h: size.h })

      const viewAsButton = (
        value: 'you' | 'teammate',
        label: string,
        enabled = true
      ) =>
        html.button(
          attr.type('button'),
          attr.class(
            who === value
              ? 'wiz-btn wiz-btn-primary'
              : 'wiz-btn wiz-btn-secondary'
          ),
          attr.disabled(!enabled),
          enabled ? attr.style('') : attr.style('opacity:0.5;'),
          on.click(() => viewAs.set(value)),
          label
        )

      // The toggle always renders: hiding it when nothing personalizes
      // would bury exactly the fact worth surfacing — that every detail
      // in this design was kept as typed.
      return html.div(
        html.div(
          attr.style(
            'display:flex; align-items:center; gap:8px; margin-bottom:10px; flex-wrap:wrap;'
          ),
          html.span(attr.class('wiz-hint'), 'Preview as:'),
          viewAsButton('you', 'You'),
          viewAsButton('teammate', 'A teammate (demo)', personalized),
          personalized
            ? who === 'teammate'
              ? html.span(
                  attr.class('wiz-hint'),
                  '“Jordan Lee” is a demo — each teammate’s own Profile fills in automatically, nothing to set up.'
                )
              : html.span()
            : html.span(
                attr.class('wiz-hint'),
                'Nothing here personalizes per teammate yet — every detail is kept exactly as typed. Go Back to fill some in from each recruiter’s Profile.'
              )
        ),
        html.div(
          attr.class('wiz-preview-holder'),
          ScaledPage(
            pageSize,
            html.div(
              attr.style(
                'position:absolute; inset:0; background:white; box-shadow:0 1px 6px rgba(0,0,0,0.15);'
              ),
              // Placeholder resume lines so the page doesn't read as broken
              // where the real resume will show through.
              html.div(
                attr.style(
                  `position:absolute; left:${rendered.placementRect.x + 24}px; top:${rendered.placementRect.y + 24}px; width:${rendered.placementRect.width - 48}px;`
                ),
                attr.innerHTML(placeholderResumeLines())
              ),
              html.div(
                attr.style('position:absolute; inset:0; pointer-events:none;'),
                attr.innerHTML(rendered.html)
              )
            )
          )
        ),
        html.p(
          attr.class('wiz-hint'),
          attr.style('margin-top:10px;'),
          'The gray lines stand in for the resume itself.',
          personalized && who === 'you'
            ? ' Try “A teammate” above to see how recruiter details personalize.'
            : ''
        )
      )
    }
  )
}

/** Does any text component reference a {{recruiter.*}} token? */
function hasRecruiterTokens(design: SavedDesign): boolean {
  return JSON.stringify(design).includes('{{recruiter.')
}

/**
 * Fixed demo persona for the teammate view. The email domain follows the
 * agency's own (from its email, else its website) so the demo reads as a
 * colleague, not lorem ipsum.
 */
function sampleTeammate(
  tokenData: TokenResolutionData
): NonNullable<TokenResolutionData['recruiter']> {
  const emailDomain = tokenData.agency?.email?.split('@')[1]?.trim()
  const urlDomain = tokenData.agency?.url
    ?.toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    ?.trim()
  const domain = emailDomain || urlDomain || 'example.com'
  return {
    name: 'Jordan Lee',
    phone: '(555) 014-9822',
    email: `jordan@${domain}`,
    linkedIn: 'linkedin.com/in/jordan-lee',
  }
}

function placeholderResumeLines(): string {
  const line = (w: number, h = 10, mb = 10) =>
    `<div style="width:${w}%; height:${h}px; margin-bottom:${mb}px; background:#e5e7eb; border-radius:4px;"></div>`
  const section = () =>
    line(30, 14, 12) + line(92) + line(86) + line(90) + line(60, 10, 26)
  return `<div>${line(45, 18, 18)}${section()}${section()}${section()}</div>`
}
